import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useFormDraft } from "../lib/formDraft";
import {
  Button, Input, FormField, Dialog, DialogCancel,
  Spinner, Empty, PageHeader, SearchInput,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui";
import { Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function emptyForm() {
  return {
    nombre: "", ruc: "", codigo: "", contacto_nombre: "",
    telefono: "", email: "", portal_url: "",
  };
}

export default function Aseguradoras() {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [open,    setOpen]    = useState(false);
  const [edit,    setEdit]    = useState(null);
  const [form,    setForm]    = useState(emptyForm());
  const [saving,  setSaving]  = useState(false);
  const draft = useFormDraft("aseguradora", form, () => setForm(emptyForm()));

  const load = useCallback(async () => {
    const r = await api.get("/aseguradoras");
    setList(r.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function openEdit(a) {
    setEdit(a);
    setForm({
      nombre:          a.nombre           ?? "",
      ruc:             a.ruc              ?? "",
      codigo:          a.codigo           ?? "",
      contacto_nombre: a.contacto_nombre  ?? "",
      telefono:        a.telefono         ?? "",
      email:           a.email            ?? "",
      portal_url:      a.portal_url       ?? "",
    });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const body = {
      nombre:          form.nombre,
      ruc:             form.ruc             || null,
      codigo:          form.codigo          || null,
      contacto_nombre: form.contacto_nombre || null,
      telefono:        form.telefono        || null,
      email:           form.email           || null,
      portal_url:      form.portal_url      || null,
    };
    try {
      if (edit) {
        await api.put(`/aseguradoras/${edit.id}`, body);
        toast.success("Aseguradora actualizada");
      } else {
        await api.post("/aseguradoras", body);
        toast.success("Aseguradora creada");
        draft.clear();
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(a) {
    if (!window.confirm(`¿Eliminar "${a.nombre}"?`)) return;
    try {
      await api.delete(`/aseguradoras/${a.id}`);
      toast.success("Aseguradora eliminada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al eliminar");
    }
  }

  const filtered = list.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.nombre?.toLowerCase().includes(q) ||
      a.ruc?.toLowerCase().includes(q) ||
      a.codigo?.toLowerCase().includes(q)
    );
  });

  if (loading) return <div className="flex items-center justify-center h-full"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Aseguradoras" sub={`${list.length} registros`}>
        <Button onClick={() => { setEdit(null); setForm({ ...emptyForm(), ...draft.load() }); setOpen(true); }}>
          <Plus className="w-4 h-4" /> Nueva aseguradora
        </Button>
      </PageHeader>

      <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nombre, RUC o código…" className="w-full sm:w-72 mb-4" />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>RUC</TableHead>
            <TableHead>Código</TableHead>
            <TableHead>Contacto</TableHead>
            <TableHead>Teléfono</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Portal</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={8}>
                <Empty message="Sin aseguradoras registradas" icon={ShieldCheck} />
              </TableCell>
            </TableRow>
          )}
          {filtered.map((a) => (
            <TableRow key={a.id}>
              <TableCell className="font-medium text-sm">{a.nombre}</TableCell>
              <TableCell className="font-mono text-xs">{a.ruc ?? "—"}</TableCell>
              <TableCell className="text-xs">{a.codigo ?? "—"}</TableCell>
              <TableCell className="text-sm">{a.contacto_nombre ?? "—"}</TableCell>
              <TableCell className="text-sm">{a.telefono ?? "—"}</TableCell>
              <TableCell className="text-sm">{a.email ?? "—"}</TableCell>
              <TableCell className="text-xs text-muted-foreground truncate max-w-[140px]">
                {a.portal_url
                  ? <a href={a.portal_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">Abrir</a>
                  : "—"}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  <button onClick={() => openEdit(a)} className="text-xs text-primary hover:underline">Editar</button>
                  <button onClick={() => handleDelete(a)} className="text-xs text-destructive hover:underline">Eliminar</button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        values={form}
        draft={edit ? undefined : draft}
        title={edit ? "Editar aseguradora" : "Nueva aseguradora"}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Nombre *" className="col-span-full">
              <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required />
            </FormField>
            <FormField label="RUC">
              <Input value={form.ruc} onChange={(e) => s("ruc", e.target.value)} placeholder="Ej: 80012345-6" />
            </FormField>
            <FormField label="Código interno">
              <Input value={form.codigo} onChange={(e) => s("codigo", e.target.value)} placeholder="Ej: MAPFRE" />
            </FormField>
            <FormField label="Nombre de contacto">
              <Input value={form.contacto_nombre} onChange={(e) => s("contacto_nombre", e.target.value)} />
            </FormField>
            <FormField label="Teléfono">
              <Input value={form.telefono} onChange={(e) => s("telefono", e.target.value)} />
            </FormField>
            <FormField label="Email">
              <Input type="email" value={form.email} onChange={(e) => s("email", e.target.value)} />
            </FormField>
            <FormField label="URL del portal" className="col-span-full">
              <Input type="url" value={form.portal_url} onChange={(e) => s("portal_url", e.target.value)} placeholder="https://..." />
            </FormField>
          </div>
          <div className="flex justify-end gap-2">
            <DialogCancel />
            <Button type="submit" loading={saving}>Guardar</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
